import { useEffect, useMemo, useRef } from "react";
import { FileUploaderResponse, FormikErrorMessage, FormikFileUpload, FormikSelect, FormikTextField, FormikTextInput } from "../../../../../../../components/widgets";
import { useDOCLabelFormView } from "./DOCLabelFormContextProvider";
import { FieldArray, useFormikContext } from "formik";
import { findIndex } from "lodash";
import { AttachmentCard } from "../../../../../../core/attachments/AttachmentCard";
import { DOCHourRatingDropdown } from "../../../form";
import { DOCLabelSIRSelector } from "./DOCLabelSIRSelector";
import { IDOCLabelApplication } from "../core";

export const DOCLabelFormApplicationDetails = () => {

  const docsTimerRef: any = useRef();
  
  const { values, setFieldValue, validateField, setFieldTouched } = useFormikContext();

  const { initValues,  units, dlmTypeCode, labelSetOptions } = useDOCLabelFormView();
  const { appType, labelSet: origLabelSet, coverageArea: origCoverage, totalLabelQuantity: origLabelCount, requestMode } = initValues;


  const unitOptions = useMemo(() => {
    if (! (units && units.length > 0 && ['DOC','MandatoryDOC','SiteInspect','LabelInspect'].includes(appType))) return [];
    return units.map(o => { return { value: o, text: o}});
  }, [appType, units]);

  const numLabelSets = labelSetOptions.length;

  const onSelectLabelSet = (e: any) => {
    if (! (e.target.value && e.target.value.length > 0 && labelSetOptions && labelSetOptions.length > 0)) return;

    const _labelSet = parseInt(`${e.target.value}`)
    const _idx = findIndex(labelSetOptions, { value: _labelSet });
    if (! (_idx > -1)) return;

    setFieldValue('hourRating', labelSetOptions[_idx].hourRating);
    let threshold = labelSetOptions[_idx].threshold;
    if (_labelSet === origLabelSet) {
      threshold += (origCoverage !== undefined && origCoverage > 0 ? origCoverage : 0) + (origLabelCount !== undefined && origLabelCount > 0 ? origLabelCount : 0);
    }
    setFieldValue('threshold', threshold);
  }

  const { approvalAttachment, poHolder, caHolder, doHolder, labelRecord, otherAttachments } = values as IDOCLabelApplication;

  const docsRequired  = useMemo(() => {
    return ((! ['SiteInspect','TriggerTest','LabelInspect','Label1b1','Label1b2','Label1b3'].includes(appType)) && ((approvalAttachment + poHolder + caHolder + labelRecord) === 0));
  }, [appType, approvalAttachment, poHolder, caHolder, labelRecord ]);

  const numOtherAttachments = useMemo(() => {
    return otherAttachments && otherAttachments.length > 0 ? otherAttachments.length : 0
  }, [otherAttachments]);

  useEffect(()=> {
    if (['SiteInspect','TriggerTest','LabelInspect'].includes(appType)) return;
    setFieldValue('hasRequiredDocs', (Math.sign(approvalAttachment) + Math.sign(poHolder) + Math.sign(caHolder) + Math.sign(labelRecord) + Math.sign(doHolder)));
    setFieldTouched('hasRequiredDocs', true, true);
    if (docsTimerRef.current) {
      clearTimeout(docsTimerRef.current);
    }
    docsTimerRef.current = setTimeout(() => {      
      validateField('hasRequiredDocs');
    }, 200);

    return () => {
      if (docsTimerRef.current) {
        clearTimeout(docsTimerRef.current);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appType, approvalAttachment, poHolder, caHolder, labelRecord, doHolder]);

    
  return (
  <>
  <div className='fw-bold text-muted fs-6 mb-2'>
    APPLICATION DETAILS
  </div>

  <div className="mb-2 row gy-2">
    <FormikTextInput size="12 col-md-6" label="Model" name="model" required={true}/>
    <FormikTextInput size="6 col-md-3" label="PO Number" name="poNO" required />
    <FormikFileUpload size="12 col-md-3 mt-3" label="Your Company PO" name="podo" acceptFiles="ApplicantFileFormats" required />      
  </div>


  { (['SiteInspect','TriggerTest','LabelInspect'].includes(appType)) ? (
    <>
    { /* SECTION : SITE INSPECTION TYPE SECTION */}
    <div className="mb-2 row gy-2">
      

      {(appType === 'SiteInspect' && dlmTypeCode === 'TYPE_DOC') &&
      <>
      <FormikTextInput size="6 col-md-3" label="Coverage Area" name="coverageArea" required={true} decimals={2} title="Coverage Area up to 2 decimal places" readOnly={requestMode === 'edit'} />
      <FormikSelect size="6 col-md-3" label="Unit" name="unit" options={unitOptions} required={true} />
      <FormikTextInput size="6 col-md-3" label="Percent Completion" name="percentCompletion" required={true} decimals={0} title="Percentage of Completion" />
      </>
      }
      
      {numLabelSets === 1 ? (
      <FormikTextInput size="6 col-md-3" label="Hour Rating" name="hourRating" readOnly />
      ) : (
      <FormikSelect size="6 col-md-3" label="Hour Rating" name="labelSet" options={labelSetOptions} required={true} onChange={onSelectLabelSet} readOnly={['edit','edit_site'].includes(requestMode)} />
      )}
      
      {((appType !== 'TriggerTest') && (dlmTypeCode === 'TYPE_LABEL') && (! ['resubmit','resubmit_site','edit','edit_site'].includes(requestMode))) &&       
      <FormikTextInput size="6 col-md-3" label="Total Label Quantity" name="totalLabelQuantity" required={true} decimals={0} />
      }
      
      {(appType === 'TriggerTest') &&
      <>
      <FormikTextField size="12 col-md-6" label="Sampling Location" name="samplingLocation" rows={1} />
      { dlmTypeCode === 'TYPE_LABEL' &&
      <>
      {numLabelSets === 1 ? (
      <FormikTextInput size="6 col-md-3" label="Hour Rating" name="hourRating" readOnly />
      ) : (
      <FormikSelect size="6 col-md-3" label="Hour Rating" name="labelSet" options={labelSetOptions} required={true} onChange={onSelectLabelSet} />
      )}
      </>
      }
      </>
      }    
    </div>
    </>
  ) : (
    <>
    { /* SECTION : NON SITE INSPECTION TYPE SECTION */}
    <div className="mb-2 row gy-2">      
      { (appType === 'DOC' || appType === 'MandatoryDOC') ? (
      <>
      <FormikTextInput size="6 col-md-2" label="Coverage Area" name="coverageArea" required={appType !== 'MandatoryDOC'} decimals={2} title="Coverage Area up to 2 decimal places" alwaysShowError={true} readOnly={requestMode === 'edit'} />
      <FormikSelect size="6 col-md-2" label="Unit" name="unit" options={unitOptions} required={true} />
      <DOCHourRatingDropdown size="6 col-md-2" label="Hour Rating" name="hourRating" required={true} readOnly={['edit','edit_site'].includes(requestMode)} />      
      { (appType === 'DOC') && 
      <DOCLabelSIRSelector />
      }
      </>
      ) : (
      <>
      {numLabelSets === 1 ? (
      <FormikTextInput size="6 col-md-3" label="Hour Rating" name="hourRating" readOnly />
      ) : (
      <FormikSelect size="6 col-md-3" label="Hour Rating" name="labelSet" options={labelSetOptions} required={true} onChange={onSelectLabelSet} readOnly={['edit','edit_site'].includes(requestMode)} />
      )}
      {((appType !== 'TriggerTest') && (dlmTypeCode === 'TYPE_LABEL') && (! ['resubmit','resubmit_site','edit','edit_site'].includes(requestMode))) &&       
      <FormikTextInput size="6 col-md-3" label="Total Label Quantity" name="totalLabelQuantity" required={true} decimals={0} />
      }
      </>      
      )}      
    </div>          
    <div className='separator border-gray-200 my-2'></div>
    <div className="row">
      <div className="fw-bold fs-6">Supporting Document(s) for the Request:</div>
      <FormikErrorMessage name='hasRequiredDocs' />      
    </div>
    <div className="mb-2 row">
      <FormikFileUpload size="12 col-md-6" label="1.  SCDF Notice Of Approval" name="approvalAttachment" acceptFiles="ApplicantFileFormats"
        required={docsRequired}
      />
      <FormikFileUpload size="12 col-md-6" label="2.  Purchase Order From Purchaser to COC Holder" name="poHolder" acceptFiles="ApplicantFileFormats"
        required={docsRequired}
      />
      <FormikFileUpload size="12 col-md-6" label="3.  Contract Agreement Between COC Holder and Purchaser" name="caHolder" acceptFiles="ApplicantFileFormats"
        required={docsRequired}
      />
      <FormikFileUpload size="12 col-md-6" label="4. Delivery Order from COC Holder to Purchaser" name="doHolder" acceptFiles="ApplicantFileFormats"
        required={docsRequired}
      />
      { (appType === 'Label5') &&
      <FormikFileUpload size="12 col-md-6" label="5.  Previous Label Distribution Record" name="labelRecord" acceptFiles="ApplicantFileFormats"      
      />    
      }
    </div>
        
    <div className="mb-2 row">
      <FormikFileUpload size="12 col-md-6" label={`Other Attachments${numOtherAttachments > 0 ? ' [' + numOtherAttachments + ']' : ''}`}     name="otherAttachments" acceptFiles="ApplicantFileFormats" isFileArray={true} />
    </div>
    <FieldArray
      name="otherAttachments"
      render={arrayHelpers => (
      <div className="d-flex flex-row flex-wrap gap-2 mh-80px overflow-auto">
      { (otherAttachments && otherAttachments.length > 0) &&
      otherAttachments.map( (o: FileUploaderResponse, idx: number) => (
      <AttachmentCard
        key={`otherAttachment${idx}`}
        className="border py-1"
        idx={idx}
        fileId={o.id}
        fileName={o.name}
        onRemove={(e: any) => {
          e.stopPropagation();
          const elem = e.target.closest('.attachment-card');
          if (elem) {
            const {idx} = elem.dataset;
            arrayHelpers.remove(idx);
          }
        }}
      />
      ))
      }
      </div>
      )}
    />
    <div className='separator border-gray-200 my-2'></div>  
    </>
  )}  
  </>   
  )
}